import { useEffect, useState, useCallback } from 'react';
import { api, downloadFile, authHeaders } from '../api';
import { StatusBadge, InvoiceBadge, formatDate, warrantyStatus, useToast } from '../components/shared';
import { RESULT, everyLabel, visitBadge } from '../components/PmLog';
import Photo from '../components/Photo';

const STATUS_COLORS = { received: 'var(--primary)', in_repair: 'var(--yellow)', done: 'var(--green)', delivered: 'var(--text3)' };
const STATUS_AR = { received: 'تم الاستلام', in_repair: 'جاري الإصلاح', done: 'جاهز', delivered: 'تم التسليم' };
const money = v => (v || v === 0) ? `${Number(v).toLocaleString('en')} EGP` : '—';
const toggleSet = (set, v) => { const s = new Set(set); s.has(v) ? s.delete(v) : s.add(v); return s; };

function TicketDetail({ id, perms, onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.portalTicket(id).then(setD).catch(() => {}); }, [id]);
  if (!d) return <div className="empty">...</div>;
  return (
    <div>
      <span className="back-btn" onClick={onBack}>→ رجوع</span>
      <div className="card mb-16" style={{ padding: 16 }}>
        <div className="flex items-center gap-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div className="fw-600" style={{ fontSize: 18 }}>{d.device_name}{d.device_alias ? <span className="text-muted"> · {d.device_alias}</span> : null}</div>
          <StatusBadge status={d.status} />
        </div>
        <div className="mono text-sm text-dim" dir="ltr" style={{ textAlign: 'right' }}>{d.device_serial}</div>
        <div className="text-sm mt-8">الاستلام: {formatDate(d.received_at)}{d.delivered_at && d.status === 'delivered' ? ` · التسليم: ${formatDate(d.delivered_at)}` : ''}</div>
        {d.fault_description && <div className="text-sm text-muted mt-4">العطل: {d.fault_description}</div>}
      </div>
      {perms.includes('parts') && d.parts.length > 0 && <>
        <div className="section-title">القطع المستخدمة</div>
        <div className="table-wrap mb-16"><table>
          <thead><tr><th>القطعة</th><th>الكمية</th><th>الضمان</th></tr></thead>
          <tbody>{d.parts.map(p => { const ws = warrantyStatus(p); return (
            <tr key={p.id}><td className="fw-500">{p.name}</td><td className="mono">{p.quantity}</td>
              <td className="text-sm">{p.warranty_months > 0 ? `${p.warranty_months} شهر${ws ? ' — ' + ws.label : ''}` : '—'}</td></tr>); })}</tbody>
        </table></div>
      </>}
      {perms.includes('invoices') && d.invoices.length > 0 && <>
        <div className="section-title">الفواتير</div>
        {d.invoices.map(inv => (
          <div key={inv.id} className="card mb-8" style={{ padding: 12 }}>
            <div className="flex items-center gap-12" style={{ flexWrap: 'wrap' }}>
              <span className="mono fw-600">{inv.invoice_number || '—'}</span>
              {inv.amount ? <span className="mono text-primary fw-600">{money(inv.amount)}</span> : null}
              <InvoiceBadge status={inv.status} />
              <span className="text-dim text-sm" style={{ marginInlineStart: 'auto' }}>{formatDate(inv.issued_at)}</span>
            </div>
          </div>
        ))}
      </>}
      {perms.includes('notes') && d.notes && <>
        <div className="section-title">ملاحظات المهندس</div>
        <div className="card" style={{ padding: 14 }}><p style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>{d.notes}</p></div>
      </>}
    </div>
  );
}

function VisitDetail({ id, onBack }) {
  const [v, setV] = useState(null);
  useEffect(() => { api.portalVisit(id).then(setV).catch(() => {}); }, [id]);
  if (!v) return <div className="empty">...</div>;
  const [l, cls] = visitBadge(v);
  return (
    <div>
      <span className="back-btn" onClick={onBack}>→ رجوع</span>
      <div className="card mb-16" style={{ padding: 16 }}>
        <div className="flex items-center gap-8" style={{ justifyContent: 'space-between' }}>
          <span className="fw-600" style={{ fontSize: 18 }}>زيارة {v.seq} من {v.visits_total}</span>
          <span className={`badge ${cls}`}>{l}</span>
        </div>
        <div className="text-sm text-muted">{formatDate(v.scheduled_date)}{v.done_at ? ` · تمت ${formatDate(v.done_at)}` : ''}</div>
      </div>
      {v.invoices && v.invoices.length > 0 && (
        <div className="card mb-16" style={{ padding: 12 }}>
          <div className="fw-600 text-sm mb-8">🧾 الفواتير</div>
          {v.invoices.map((inv, i) => (
            <div key={i} className="flex items-center gap-8 text-sm" style={{ padding: '4px 0' }}>
              <span className="mono">{inv.invoice_number || '—'}</span><span className="mono fw-600">{money(inv.amount)}</span>
              <InvoiceBadge status={inv.status} /><span className="text-xs text-dim">{formatDate(inv.issued_at)}</span>
            </div>
          ))}
        </div>
      )}
      {v.devices.map(cd => {
        const it = cd.item; const [rl, rcls] = it ? (RESULT[it.result] || [it.result, 'badge-gray']) : ['لسه', 'badge-gray'];
        return (
          <div key={cd.id} className="card mb-8" style={{ padding: 12 }}>
            <div className="flex items-center gap-8" style={{ justifyContent: 'space-between' }}>
              <span className="fw-600">{cd.name}{cd.alias ? <span className="text-muted"> · {cd.alias}</span> : null}</span>
              <span className={`badge ${rcls}`}>{rl}</span>
            </div>
            {cd.repair && <div className="text-sm mt-4" style={{ padding: 8, background: 'var(--bg2)', borderRadius: 6 }}>
              🔧 {cd.repair.fault_description}{cd.repair.parts.length ? ` · ${cd.repair.parts.map(p => `${p.name}${p.quantity > 1 ? ' ×' + p.quantity : ''}`).join('، ')}` : ''}
              {cd.repair.notes && <div className="text-xs text-muted">{cd.repair.notes}</div>}
            </div>}
            {it && <div className="text-sm mt-4" style={{ color: 'var(--text2)' }}>
              {it.checklist.map((c, i) => <div key={i}>{c.ok ? '✅' : '⬜'} {c.text}</div>)}
              {it.notes && <div className="text-muted mt-4">{it.notes}</div>}
            </div>}
          </div>
        );
      })}
    </div>
  );
}

function DeviceProfile({ id, perms, onBack, onTicket, onVisit }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.portalDevice(id).then(setD).catch(() => {}); }, [id]);
  if (!d) return <div className="empty">...</div>;
  return (
    <div>
      <span className="back-btn" onClick={onBack}>→ رجوع</span>
      <div className="card mb-16 flex gap-12" style={{ padding: 16, alignItems: 'flex-start' }}>
        {perms.includes('photos') && <Photo kind="device" id={d.id} version={d.photo_at} size={120} icon="📟" />}
        <div>
          <div className="fw-600" style={{ fontSize: 22 }}>{d.name}</div>
          {d.alias && <div className="text-muted">{d.alias}</div>}
          <div className="mono text-sm mt-4" dir="ltr" style={{ textAlign: 'right' }}>S/N: {d.serial}</div>
          {d.next_visit && <div className="text-sm mt-8">🛠 الصيانة الجاية: <b>{formatDate(d.next_visit.scheduled_date)}</b> (زيارة {d.next_visit.seq})</div>}
        </div>
      </div>

      {d.maintenance && <>
        <div className="section-title">سجل الصيانة الدورية</div>
        <div className="card mb-16" style={{ padding: 0 }}>
          {d.maintenance.length === 0 ? <div className="empty text-sm">لسه مفيش</div> : d.maintenance.map((m, i) => {
            const [l, cls] = RESULT[m.result] || [m.result, 'badge-gray'];
            return (
              <div key={i} onClick={() => onVisit(m.visit_id)} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                <div className="flex items-center gap-8" style={{ justifyContent: 'space-between' }}>
                  <span className="fw-600 text-sm">{formatDate(m.done_at)} · زيارة {m.seq}</span>
                  <span className={`badge ${cls}`}>{l}</span>
                </div>
                <div className="text-sm" style={{ color: 'var(--text2)' }}>{m.checklist.map(c => `${c.ok ? '✅' : '⬜'} ${c.text}`).join('   ')}</div>
                {m.notes && <div className="text-xs text-muted mt-4">{m.notes}</div>}
              </div>
            );
          })}
        </div>
      </>}

      {d.tickets && <>
        <div className="section-title">الإصلاحات</div>
        <div className="card" style={{ padding: 0 }}>
          {d.tickets.length === 0 ? <div className="empty text-sm">مفيش إصلاحات</div> : d.tickets.map(t => (
            <div key={t.id} className="flex items-center gap-8" onClick={() => onTicket(t.id)}
              style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
              <span className="text-sm" style={{ flex: 1 }}>{t.fault_description || '—'}</span>
              <span className="text-xs text-dim">{formatDate(t.received_at)}</span>
              <StatusBadge status={t.status} />
            </div>
          ))}
        </div>
      </>}
    </div>
  );
}

export default function ClientPortal() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [ticketId, setTicketId] = useState(null);
  const [visitId, setVisitId] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [statusF, setStatusF] = useState(new Set());
  const toast = useToast();

  useEffect(() => { api.portalMe().then(setMe).catch(() => {}); }, []);
  const perms = me ? me.permissions : [];
  const has = useCallback(p => perms.includes(p), [perms]);

  useEffect(() => {
    if (!me) return;
    api.portalDevices().then(setDevices).catch(() => {});
    if (has('tickets')) api.portalTickets().then(setTickets).catch(() => {});
    if (has('maintenance')) api.portalMaintenance().then(setContracts).catch(() => {});
  }, [me, has]);

  if (!me) return <div className="page"><div className="empty">...</div></div>;
  const tabs = [['devices', '📟 أجهزتي'], has('tickets') && ['tickets', '🔧 الإصلاحات'], has('maintenance') && ['maintenance', '🛠 الصيانة الدورية']].filter(Boolean);
  const filtered = tickets.filter(t => statusF.size === 0 || statusF.has(t.status));

  async function openFile(k) {
    try {
      const res = await fetch(`/api/portal/maintenance/${k.id}/file`, { headers: authHeaders() });
      if (!res.ok) throw new Error('الملف مش موجود');
      window.open(URL.createObjectURL(await res.blob()), '_blank');
    } catch (e) { toast(e.message, 'error'); }
  }

  return (
    <div className="page" dir="rtl">
      <div className="flex items-center gap-12 mb-16">
        {has('photos') && me.client && <Photo kind="client" id={me.client.id} version={me.client.photo_at} size={56} icon="🏥" />}
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>مرحباً <span>{me.client ? me.client.name : ''}</span></h1>
          <p className="text-dim text-sm mt-4">تابع حالة أجهزتك</p>
        </div>
      </div>

      <div className="flex gap-8 mb-16" style={{ flexWrap: 'wrap' }}>
        {tabs.map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => { setTab(k); setTicketId(null); setVisitId(null); setDeviceId(null); }}>{l}</button>
        ))}
      </div>

      {tab === 'devices' && (deviceId ? <DeviceProfile id={deviceId} perms={perms} onBack={() => setDeviceId(null)}
          onTicket={id => { setTab('tickets'); setTicketId(id); }} onVisit={id => { setTab('maintenance'); setVisitId(id); }} /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {devices.map(d => (
            <div key={d.id} className="card flex items-center gap-12" onClick={() => setDeviceId(d.id)} style={{ padding: 12, cursor: 'pointer' }}>
              {has('photos') && <Photo kind="device" id={d.id} version={d.photo_at} size={56} icon="📟" />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="fw-600">{d.name}{d.alias ? <span className="text-muted"> · {d.alias}</span> : null}</div>
                <div className="mono text-xs text-dim" dir="ltr" style={{ textAlign: 'right' }}>{d.serial}</div>
                {has('tickets') && d.last_ticket_status && (
                  <div className="text-xs mt-4" style={{ color: STATUS_COLORS[d.last_ticket_status] }}>● آخر إصلاح: {STATUS_AR[d.last_ticket_status]}</div>
                )}
              </div>
              <span className="text-dim">‹</span>
            </div>
          ))}
          {devices.length === 0 && <div className="empty">لا توجد أجهزة</div>}
        </div>
      ))}

      {tab === 'tickets' && (ticketId ? <TicketDetail id={ticketId} perms={perms} onBack={() => setTicketId(null)} /> : <>
        <div className="flex gap-8 mb-16" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          {has('export') && <button className="btn btn-ghost btn-sm" style={{ order: 99, marginInlineStart: 'auto' }}
            onClick={() => downloadFile('/api/portal/export').catch(e => toast(e.message, 'error'))}>⬇ Excel</button>}
          {Object.keys(STATUS_AR).map(s => {
            const on = statusF.has(s);
            return <button key={s} onClick={() => setStatusF(p => toggleSet(p, s))} style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `2px solid ${on ? STATUS_COLORS[s] : 'var(--border)'}`, background: on ? STATUS_COLORS[s] + '22' : 'transparent',
              color: on ? STATUS_COLORS[s] : 'var(--text3)' }}>{on ? '✓ ' : ''}{STATUS_AR[s]}</button>;
          })}
        </div>
        <div className="table-wrap"><table>
          <thead><tr><th>الجهاز</th><th>السيريال</th><th>العطل</th><th>الحالة</th><th>الاستلام</th>{has('invoices') && <th>فواتير</th>}</tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={6}><div className="empty">لا توجد بيانات</div></td></tr> : filtered.map(t => (
              <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setTicketId(t.id)}>
                <td className="fw-500">{t.device_name}{t.device_alias && <div className="text-dim text-xs">{t.device_alias}</div>}</td>
                <td><span className="mono text-sm">{t.device_serial}</span></td>
                <td className="text-sm text-muted">{t.fault_description || '—'}</td>
                <td><StatusBadge status={t.status} /></td>
                <td className="mono text-sm text-dim">{formatDate(t.received_at)}</td>
                {has('invoices') && <td className="mono text-sm">{t.invoice_count}</td>}
              </tr>
            ))}
          </tbody>
        </table></div>
      </>)}

      {tab === 'maintenance' && (visitId ? <VisitDetail id={visitId} onBack={() => setVisitId(null)} /> : <>
        {contracts.length === 0 && <div className="card"><div className="empty">مفيش عقد صيانة دورية</div></div>}
        {contracts.map(k => (
          <div key={k.id} className="card mb-16" style={{ padding: 16 }}>
            <div className="flex items-center gap-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <span className="fw-600" style={{ fontSize: 16 }}>📄 {k.title || 'عقد الصيانة الدورية'}</span>
              <span className={`badge ${k.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{k.status === 'active' ? 'ساري' : 'منتهي'}</span>
            </div>
            <div className="text-sm text-muted mt-4">{formatDate(k.start_date)} ← {formatDate(k.end_date)} · زيارة {everyLabel(k.every_value, k.every_unit)}
              {k.value !== undefined && k.value !== null ? ` · ${money(k.value)}` : ''}</div>
            <div className="text-sm mt-4">✓ {k.visits_done} من {k.visits.length} زيارة · الزيارة الجاية: <b>{k.next_visit ? formatDate(k.next_visit) : '—'}</b>
              {k.file_name && <> · <a className="link" href="#f" onClick={e => { e.preventDefault(); openFile(k); }}>📎 ملف العقد</a></>}</div>

            <div className="section-title mt-16 mb-8">الأجهزة</div>
            {k.devices.map(cd => {
              const [rl, rcls] = cd.last ? (RESULT[cd.last.result] || [cd.last.result, 'badge-gray']) : ['لسه', 'badge-gray'];
              return (
                <div key={cd.id} className="flex items-center gap-8" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className="text-sm fw-500" style={{ flex: 1 }}>{cd.name}{cd.alias ? <span className="text-muted"> · {cd.alias}</span> : null}</span>
                  <span className="text-xs text-dim">{cd.last ? formatDate(cd.last.done_at) : ''}</span>
                  <span className={`badge ${rcls}`}>{rl}</span>
                </div>
              );
            })}

            <div className="section-title mt-16 mb-8">الزيارات</div>
            <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
              {k.visits.map(v => {
                const [l, cls] = visitBadge(v);
                return (
                  <button key={v.id} className={`badge ${cls}`} style={{ cursor: 'pointer', border: 'none', padding: '6px 10px' }} onClick={() => setVisitId(v.id)}
                    title={l}>{v.seq} · {formatDate(v.scheduled_date)}{v.issues ? ' ⚠' : ''}</button>
                );
              })}
            </div>
          </div>
        ))}
      </>)}
    </div>
  );
}
